import { ButtonVariants } from "@inno-ui/button";
import { Dialog } from "@inno-ui/dialog";
import { CheckmarkIcon, Icon, IconWeight, MultiplyIcon } from "@inno-ui/icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type {
  Participant,
  ParticipantWithId,
} from "../../../../../types/general";

import { useAppForm } from "../../../../../hooks/use-form";
import { useAppointmentStore } from "../../../../../store/appointment/appointment-store";
import { AddGuestForm } from "../add-guest/add-guest-form/add-guest-form";
import { ADD_GUEST_FORM_FIELDS } from "../add-guest/add-guest-form/add-guest-form.constant";
import styles from "./added-guests.module.scss";

export function AddedGuests() {
  const { otherParticipants, deleteParticipant, updateParticipant }
    = useAppointmentStore();
  const [selectedParticipantId, setSelectedParticipantId] = useState<
    string | null
  >(null);
  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const form = useAppForm({
    ...ADD_GUEST_FORM_FIELDS,
    onSubmit: ({ value }: { value: Participant }) => {
      updateParticipant(selectedParticipantId ?? "", {
        email: value.email,
        firstName: value.firstName,
        lastName: value.lastName,
        phone: value.phone,
        gender: value.gender,
      });
      setIsDialogOpen(false);
    },
  });

  const handleEditClick = (participant: ParticipantWithId) => {
    if (isDialogOpen)
      return;
    setIsDialogOpen(true);
    setSelectedParticipantId(participant.id);
    form.setFieldValue("firstName", participant.firstName);
    form.setFieldValue("lastName", participant.lastName);
    form.setFieldValue("email", participant.email);
    form.setFieldValue("gender", participant.gender);
    // need this to ensure the phone field is set correctly
    setTimeout(() => {
      form.setFieldValue("phone", participant.phone);
    }, 0);
  };

  const handleCloseDialog = () => {
    setSelectedParticipantId(null);
    setIsDialogOpen(false);
  };

  return (
    <div className={styles.addedGuestsWrapper}>
      {otherParticipants.length > 0 && (
        <h2>{t("guests")}</h2>
      )}
      {otherParticipants.map(participant => (
        <div className={styles.addedGuest} key={participant.id}>
          {/* guest info */}
          <div className={styles.guestInfo}>
            <div className={styles.guestName}>
              <p>
                {participant.firstName}
                {" "}
                {participant.lastName}
              </p>
              <p>{participant.email}</p>
            </div>
            {/* action buttons */}
            <div className={styles.guestActions}>
              {/* edit guest */}
              <div
                className={styles.editGuest}
                onClick={() => handleEditClick(participant)}
              >
                <Icon icon="PencilLineIcon" weight={IconWeight.Bold} />
              </div>
              <div
                className={styles.deleteGuest}
                onClick={() => deleteParticipant(participant.id)}
              >
                <Icon icon="TrashLineIcon" weight={IconWeight.Bold} />
              </div>
            </div>
          </div>
        </div>
      ))}
      <Dialog
        isOpen={isDialogOpen}
        closeOnClickOutside={false}
        offset={-(otherParticipants.length > 1 ? 163 : 83)}
        onClose={handleCloseDialog}
        title={t("edit-guest")}
        // @ts-expect-error
        body={<AddGuestForm form={form} />}
        actions={[
          {
            label: "Cancel",
            icon: MultiplyIcon,
            onClick: handleCloseDialog,
          },
          {
            label: "Save",
            icon: CheckmarkIcon,
            variant: ButtonVariants.Dark,
            onClick: () => {
              form.handleSubmit();
            },
          },
        ]}
        children={<div />}
      />
    </div>
  );
}
